import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function for combining and merging CSS class names with Tailwind CSS support.
 * 
 * This function combines the power of `clsx` for conditional class name handling
 * with `tailwind-merge` for resolving Tailwind CSS class conflicts. It's essential
 * for building dynamic, conflict-free class names in React components.
 * 
 * @param inputs - Variable number of class name inputs (strings, objects, arrays, etc.)
 * @returns A single merged class name string with conflicts resolved
 * 
 * @public
 * @category Utils
 * @since 1.0.0
 * 
 * @example Basic Usage
 * ```typescript
 * import { cn } from './utils';
 * 
 * const className = cn('px-4', 'py-2', 'bg-blue-500');
 * // Result: "px-4 py-2 bg-blue-500"
 * ```
 * 
 * @example Conditional Classes
 * ```typescript
 * const isActive = true;
 * const className = cn(
 *   'base-class',
 *   { 'active-class': isActive },
 *   isActive && 'another-active-class'
 * );
 * ```
 * 
 * @example Tailwind Conflict Resolution
 * ```typescript
 * const className = cn('px-4', 'px-6'); // px-6 wins
 * const className2 = cn('bg-red-500', 'bg-blue-500'); // bg-blue-500 wins
 * ```
 * 
 * @remarks
 * This utility is particularly useful for:
 * - Building reusable component class name logic
 * - Handling conditional styling based on props or state
 * - Resolving Tailwind CSS class conflicts automatically
 * - Maintaining clean, readable component code
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
